/**
 * Assembles and boots the main-thread Redux store for the Redact app.
 *
 * Combines the redact/common/case-media/onboarding/permissions reducer maps with the
 * redux-first-router reducer, and wires middleware in order: redux-api-middleware, the
 * workeredux bridge (src/utils/workeredux/client/createMiddleware.ts), the router, and
 * redux-saga (sagas wrapped in keepAlive so a crash restarts them); only the saga
 * middleware is per store. Router/history, the Worker and the module-level `store`
 * (used by displayConfirmLeave) stay shared. Also spawns the second Redux store's Web Worker
 * (src/common-app/web-worker/worker.ts) at module load, before configureAppStore() is
 * called, and posts it an APP_CONFIG message carrying the Sentry config plus the
 * `redact-log` redux-logging flag (a worker can't read localStorage itself).
 * Dispatches boot() on start. The store it returns carries `stop()`, which cancels
 * that store's root sagas; the app never calls it, unit tests do after each test.
 */
import { createContext } from 'react';
import { find, isString } from 'lodash';
import queryString from 'query-string';
import { configureStore, Middleware, Store } from '@reduxjs/toolkit';
import { apiMiddleware } from 'redux-api-middleware';
import { connectRoutes, redirect } from 'redux-first-router';
import createSagaMiddleware from 'redux-saga';
// TODO: What can we import from reduxjs/toolkit instead to break explicit dependency on redux-thunk?
// See below - this may already be included by default in the middleware array.
// import { thunk } from 'redux-thunk';
import { createReduxWorkerMiddleware } from '@utils';

import routes from '@redact-pages/routes';
import { normalizeIngestUrlInLocation } from '@redact/state/normalizeIngestUrl';
import { redactReducers } from '@redact-modules/rootReducer';
import { caseMediaReducer } from '@redact-modules/casemedia/reducers';
import { settingsProfileReducer } from '@redact/state/modules/settingsProfile/reducers';
import { redactionCodesReducer } from '@redact-modules/redactionCodes/reducers';
import { lexiconsReducer } from '@redact-modules/lexicons/reducers';
import { watermarksReducer } from '@redact-modules/watermarks/reducers';
import { trashReducer } from '@redact-modules/trash/reducers';
import { adminReducer } from '@redact-modules/admin/reducers';
import { redactionStatusesReducer } from '@redact-modules/redactionStatuses/reducers';
import { statusHistoryReducer } from '@common-modules/statusHistory/reducers';
import { downloadsReducer } from '@redact-modules/downloads';
import { adminDependenciesReducer } from '@redact-modules/adminDependencies';
import { userOnboardingReducers, userOnboardingSagas } from '@user-onboarding';
import { userPermissionsReducers } from '@user-permissions';

import { boot } from '@common-modules/app';
import {
  HIDE_BLOCK_NAVIGATION_MODAL,
  SHOW_BLOCK_NAVIGATION_MODAL,
} from '@common-modules/mediaDetails';
import composeReducers from '@common-state/rootReducer';
import { sagas } from '@common-state/sagas';
import { keepAlive } from '@common/shared/keepSagasAlive';

/**
 * The app store, plus the hook that shuts it down again. Stopping is part of a
 * store's lifecycle, not a test backdoor: the app builds one store and never
 * stops it, while a unit test builds one per test and must.
 */
export type AppStore = Store & { stop: () => void };

let store: AppStore;

// VE-18631: repair a deep-linked ingest URL (its signature survives redux-first-router's
// `split('?')` truncation) BEFORE connectRoutes reads window.location. See normalizeIngestUrl.ts.
normalizeIngestUrlInLocation();

const {
  reducer: routerReducer,
  middleware: routerMiddleware,
  enhancer: routerEnhancer,
  initialDispatch,
} = connectRoutes(routes, {
  querySerializer: queryString,
  initialDispatch: false,
  onBeforeChange: (dispatch, getState, { action }) => {
    const routeDefinition = routes[action.type];

    if (!isString(routeDefinition) && routeDefinition?.redirects) {
      const matchedRedirect = find(
        routeDefinition.redirects,
        ({ test }) => !!test(getState, action)
      );

      matchedRedirect && dispatch(redirect(matchedRedirect.to));
    }
  },
  displayConfirmLeave: (message, callback) => {
    const canLeave = (can: boolean) => {
      store.dispatch(HIDE_BLOCK_NAVIGATION_MODAL());
      return callback(can); // navigate to next route or stay where ur at
    };

    store.dispatch(SHOW_BLOCK_NAVIGATION_MODAL({ message, canLeave }));
  },
});

// Opt-in redux logging for deployed environments — see "Redux action logging" in the root
// README. try/catch: localStorage getters throw when storage is blocked (privacy modes,
// partitioned third-party storage). This runs at module load, before any error boundary
// exists, so an unguarded throw here would be an unrecoverable blank page.
const readRedactLogFlag = (): boolean => {
  try {
    return localStorage.getItem('redact-log') === 'true';
  } catch {
    return false;
  }
};

const isDev = process.env.NODE_ENV === 'development';
const redactLogEnabled = readRedactLogFlag();

const worker = new Worker(new URL('@worker/worker', import.meta.url));
worker.postMessage({
  type: 'APP_CONFIG',
  config: {
    sentry: {
      dsn: window.config?.sentryDSN,
      environment: window.config?.nodeEnv,
      tracesSampleRate: 1.0,
    },
    redactLog: redactLogEnabled,
  },
});

const { reduxWorkerMiddleware } = createReduxWorkerMiddleware(worker);

// Shared by every store; the router and worker ones wrap module singletons.
const sharedMiddlewares = [
  apiMiddleware,
  reduxWorkerMiddleware,
  routerMiddleware,
  // TODO: The configureAppStore flags this as a duplicate if uncommented.
  // Assuming it's already included by default, need to confirm and then remove this.
  // thunk,
];

const loggerMiddlewares: Middleware[] = [];
if (isDev || redactLogEnabled) {
  // TODO: get types for reuux-logger
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { createLogger } = require(`redux-logger`);

  loggerMiddlewares.push(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    createLogger({
      collapsed: true,
    })
  );
}

export default function configureAppStore(): AppStore {
  // Per store: a shared one shares its channel, so stores would run each
  // other's sagas (VE-29544).
  const sagaMiddleware = createSagaMiddleware();

  const baseStore = configureStore({
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    reducer: composeReducers({
      ...redactReducers,
      ...caseMediaReducer,
      ...redactionCodesReducer,
      ...lexiconsReducer,
      ...trashReducer,
      ...adminReducer,
      ...redactionStatusesReducer,
      ...statusHistoryReducer,
      ...downloadsReducer,
      ...adminDependenciesReducer,
      ...userOnboardingReducers,
      ...userPermissionsReducers,
      ...settingsProfileReducer,
      ...watermarksReducer,
      location: routerReducer,
    }),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).prepend(...sharedMiddlewares, sagaMiddleware, ...loggerMiddlewares),
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().prepend(routerEnhancer as any),
    devTools: process.env.NODE_ENV !== 'production',
  });

  const rootSagaTasks = [
    sagaMiddleware.run(keepAlive(sagas)),
    sagaMiddleware.run(keepAlive([userOnboardingSagas])),
  ];

  store = Object.assign(baseStore, {
    // Not undone: route sagas the roots spawned, the shared history/worker
    // listeners, and this module-level `store`.
    stop: () => rootSagaTasks.forEach((task) => task.cancel()),
  });

  initialDispatch?.();
  store.dispatch(boot());

  return store;
}

// @ts-expect-error TODO: no value was passed originally but it's required by type
// see https://github.com/DefinitelyTyped/DefinitelyTyped/pull/24509#issuecomment-382213106
export const StoreContext = createContext();
